import { cn } from '@ui-kit/utils';
import { domAnimation, LazyMotion, m, motion, type Transition, useAnimation } from 'motion/react';
import {
  createContext,
  createElement,
  type DetailsHTMLAttributes,
  type ElementType,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactEventHandler,
  useContext,
  useEffect,
  useRef,
} from 'react';
import ChevronDownIcon from '@/components/icons/ChevronDownIcon';

const DURATION = 0.3;

const transition: Transition = {
  type: 'tween',
  duration: DURATION,
};

const MotionChevron = motion.create(ChevronDownIcon);

type AccordionContextValue = {
  isRounded: boolean;
  isTransparent: boolean;
};

const AccordionContext = createContext<AccordionContextValue>({
  isRounded: false,
  isTransparent: false,
});

type AccordionProps = HTMLAttributes<HTMLDivElement> &
  ({ isRounded?: true; isTransparent?: false } | { isRounded?: false; isTransparent?: boolean });

export const Accordion = ({
  isRounded = false,
  isTransparent = false,
  className,
  children,
  ...restProps
}: AccordionProps) => (
  <AccordionContext.Provider value={{ isRounded, isTransparent }}>
    <div className={cn('flex flex-col', isRounded ? 'gap-4' : 'gap-0', className)} {...restProps}>
      {children}
    </div>
  </AccordionContext.Provider>
);

export type AccordionItemProps = Omit<DetailsHTMLAttributes<HTMLDetailsElement>, 'open'> & {
  label: string | { value: string; as: ElementType };
  asideLabel?: string;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

const useAccordionItemClassNames = () => {
  const { isRounded, isTransparent } = useContext(AccordionContext);

  return {
    details: cn(
      'overflow-hidden',
      isTransparent ? 'bg-transparent' : 'bg-surface-default',
      isRounded ? 'rounded-lg' : 'border-border-default border-b',
    ),
    summary: cn(
      '[&_svg]:text-icon-subtle flex cursor-pointer list-none items-center justify-between px-6 py-4 lg:py-4.5 [&::-webkit-details-marker]:hidden',
      isRounded ? 'lg:px-4' : 'lg:px-0',
    ),
    content: cn('px-6 pb-4', isRounded ? 'lg:px-4' : 'lg:px-0'),
  };
};

export const AccordionItem = ({
  label,
  asideLabel,
  isOpen,
  children,
  onOpen,
  onClose,
  className,
  ...restProps
}: AccordionItemProps) => {
  const controls = useAnimation();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const classNames = useAccordionItemClassNames();

  const handleHasOpened = () => {
    if (isOpen === undefined) {
      void controls.start('open');
    }

    onOpen?.();
  };

  const handleRequestClose = () => {
    if (isOpen === undefined) {
      void controls.start('closed').then(() => {
        if (detailsRef.current) {
          detailsRef.current.open = false;
        }
      });
    }

    onClose?.();
  };

  useEffect(
    function handleControlledState() {
      if (isOpen) {
        if (detailsRef.current) {
          detailsRef.current.open = true;
        }
        void controls.start('open');
      } else if (isOpen === false) {
        void controls.start('closed').then(() => {
          if (detailsRef.current) {
            detailsRef.current.open = false;
          }
        });
      }
    },
    [isOpen, controls],
  );

  const handleToggle: ReactEventHandler<HTMLDetailsElement> = () => {
    if (detailsRef.current?.open) {
      handleHasOpened();
    }
  };

  const handleSummaryClick: MouseEventHandler<HTMLElement> = event => {
    if (detailsRef.current?.open) {
      event.preventDefault();
      handleRequestClose();
    }
  };

  const initial = isOpen ? 'open' : 'closed';
  const labelClassName = 'text-body-regular';

  return (
    <LazyMotion features={domAnimation}>
      <details
        ref={detailsRef}
        className={cn(classNames.details, className)}
        onToggle={handleToggle}
        {...restProps}
      >
        <summary className={classNames.summary} onClick={handleSummaryClick}>
          {typeof label === 'string'
            ? createElement('span', { className: labelClassName }, label)
            : createElement(label.as, { className: labelClassName }, label.value)}

          <div className="flex items-center gap-2">
            {asideLabel ? <span className={labelClassName}>{asideLabel}</span> : null}

            <MotionChevron
              aria-hidden="true"
              className="size-6 shrink-0"
              initial={initial}
              animate={controls}
              variants={{
                open: { rotate: -180 },
                closed: { rotate: 0 },
              }}
              transition={transition}
            />
          </div>
        </summary>

        <m.div
          initial={initial}
          animate={controls}
          variants={{
            open: { height: 'auto' },
            closed: { height: 0 },
          }}
          transition={transition}
          className="overflow-hidden"
        >
          <div className={classNames.content}>{children}</div>
        </m.div>
      </details>
    </LazyMotion>
  );
};
